import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { AiContractExtractionService } from '@core/services/ai/ai-contract-extraction.service';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { ExtractionJobStatus } from '@shared/definitions/contracts/extraction-result.model';

/**
 * App-wide tracker for in-flight AI contract extractions.
 *
 * The add-contract screen queues a background extraction job and watches its
 * Firestore doc locally — but that subscription dies the moment the user
 * navigates away. This root-provided service keeps watching every job the
 * session started, so a global toolbar indicator can show "the AI is working"
 * regardless of where the user is in the app.
 *
 * State is intentionally session-scoped: it survives in-app (router)
 * navigation but resets on a full page reload. That's fine — the extraction
 * still completes server-side and the existing in-app/push notification fires
 * on completion; only the spinning star is lost on a hard refresh.
 */
@Injectable({ providedIn: 'root' })
export class ExtractionActivityService {
  private readonly aiExtraction = inject(AiContractExtractionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly workspaceStore = inject(WorkspaceStore);

  /** jobId → latest known status for every job this session is watching. */
  private readonly _active = signal<ReadonlyMap<string, ExtractionJobStatus>>(new Map());
  /** Live Firestore subscriptions, keyed by jobId (detached on terminal state). */
  private readonly subs = new Map<string, Subscription>();

  private readonly _latestJobId = signal<string | null>(null);

  /** True while at least one tracked job is queued or processing. */
  readonly isExtracting = computed(() =>
    [...this._active().values()].some(isRunning)
  );

  /** How many tracked jobs are currently queued/processing. */
  readonly activeCount = computed(() =>
    [...this._active().values()].filter(isRunning).length
  );

  /**
   * The most recently started job that is still running — drives the
   * "click the star to jump back to the extraction" deep-link.
   */
  readonly latestJobId = this._latestJobId.asReadonly();

  /**
   * Begin tracking an extraction job. Idempotent per jobId. The job is marked
   * running immediately (so the indicator appears the instant an upload is
   * queued, before the doc exists), then driven by the realtime job doc. The
   * listener auto-detaches once the job reaches a terminal state.
   */
  track(jobId: string): void {
    if (!jobId || this.subs.has(jobId)) return;

    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) return;

    this._latestJobId.set(jobId);
    this.setStatus(jobId, 'queued');

    const sub = this.aiExtraction.watchExtractionJob(workspaceId, jobId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (job) => {
        if (!job) return;
        this.setStatus(jobId, job.status);
        if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
          this.stop(jobId);
        }
      },
      // A read error shouldn't leave the indicator spinning forever.
      error: () => this.stop(jobId)
    });
    this.subs.set(jobId, sub);
  }

  private setStatus(jobId: string, status: ExtractionJobStatus): void {
    this._active.update((m) => new Map(m).set(jobId, status));
  }

  private stop(jobId: string): void {
    this.subs.get(jobId)?.unsubscribe();
    this.subs.delete(jobId);
    this._active.update((m) => {
      const next = new Map(m);
      next.delete(jobId);
      return next;
    });
    if (this._latestJobId() === jobId) {
      // Re-point the deep-link at any remaining running job, else clear it.
      const remaining = [...this._active().keys()];
      this._latestJobId.set(remaining.length ? remaining[remaining.length - 1] : null);
    }
  }
}

function isRunning(status: ExtractionJobStatus): boolean {
  return status === 'queued' || status === 'processing';
}
